<template>
  <section class="section-base form-section-base">
    <div class="form-section-base__title">
      Сведения о должнике (если должников несколько, раздел заполняется в
      отношении каждого должника)
    </div>

    <v-container>
      <div class="pb-5">
        <Debtor
          v-for="(v, index) in templateDebtors"
          :key="index"
          @delete="deleteDebtor(index)"
          @parsed-mrpa-change="parsedMrpaChangeHandler($event, index)"
          :v="v"
          :can-delete="debtors.length > 1"
          :role-type="roleType"
          class="debtor"
        />

        <v-btn depressed color="primary" class="button-base" @click="addDebtor">
          <v-icon size="20" left>{{ mdiAccountMultiplePlus }}</v-icon>
          Добавить должника
        </v-btn>
      </div>

      <Footer
        @next-btn-click="
          () =>
            leaveStep({
              stepThis: this,
              direction: StepDirection.NEXT,
              step: StepName.S1,
            })
        "
        :loading-next="loadingNext"
        :first-step="true"
      />
    </v-container>
  </section>
</template>

<script>
import cloneDeep from 'clone-deep';
import deepmerge from 'deepmerge';
import { mdiAccountMultiplePlus } from '@mdi/js';

import { getValidationErrorText } from '@/utils/validate';
import { Role } from '@/utils/dictionaries';
import Footer from '@/components/services/elements/Footer';
import { leaveStep } from '@/utils/services';
import { ServicesModule } from '@/store/modules/services';
import { StepDirection, StepName } from '@/types/services';
import Debtor, { debtorModel, getDebtorValidation } from './Debtor';
import { MrpaType, StatementType, SubjectFillType } from '@/constants';
import { creditorModel } from '@/components/services/AcceptanceEscrow/Step2/Creditor';
import {
  getMrpa,
  prefillMrpDisabled,
} from '@/components/services/elements/MrpaUploader';
import {
  assemblyManualTrustee,
  getDefaultManualTrustee,
} from '@/components/services/elements/TrusteeView';
import {
  assemblyMrpaTrustee,
  getDefaultMachineTrustee,
} from '@/components/services/elements/MrpaTrustee';
import { MrpaSubjectModule } from '@/store/modules/mrpaSubject';

export default {
  name: 'Step1',
  props: {
    loadingNext: {
      type: Boolean,
      required: true,
    },
    roleType: {
      type: String,
      required: true,
    },
  },
  components: {
    Debtor,
    Footer,
  },
  data: () => ({
    StepDirection,
    StepName,
    leaveStep,
    Role,
    mdiAccountMultiplePlus,
    getValidationErrorText,
    invalidFields: [],
    debtors: [cloneDeep(debtorModel)],
  }),
  validations() {
    const validation = {
      debtors: {},
    };

    this.debtors.forEach((debtor, index) => {
      validation.debtors[index] = getDebtorValidation(debtor);
    });

    return validation;
  },
  computed: {
    templateDebtors() {
      const debtors = [];

      for (const key in this.$v.debtors) {
        if (
          Object.prototype.hasOwnProperty.call(this.$v.debtors, key) &&
          !key.startsWith('$')
        ) {
          debtors.push(this.$v.debtors[key]);
        }
      }

      return debtors;
    },
  },
  created() {
    this.prefillDraft();
  },
  methods: {
    prefillDraft() {
      if (!ServicesModule.statement.subjects.debtors?.length) {
        return;
      }

      this.debtors = ServicesModule.statement.subjects.debtors.map(
        (debtor, i) => {
          const fillType = debtor.uiContext?.fillType ?? SubjectFillType.MANUAL;

          return {
            ...cloneDeep(debtorModel),
            number: i + 1,
            fillType,
            manual: this.getManualModel(debtor, fillType),
            machine: this.getMachineModel({ debtor, fillType }),
          };
        }
      );
    },

    getManualModel(debtor, fillType) {
      if (fillType === SubjectFillType.MACHINE) {
        return cloneDeep(debtorModel.manual);
      }

      const fromEsia = debtor.details?.fromEsia;

      if (debtor.person) {
        return deepmerge(debtorModel.manual, {
          role: Role.PERSON,
          person: debtor.person,
          fromEsia,
        });
      }

      if (debtor.organization) {
        return deepmerge(debtorModel.manual, {
          role: Role.ORGANIZATION,
          organization: {
            ...debtor.organization,
            trustee: getDefaultManualTrustee(debtor.trustee),
          },
          fromEsia,
        });
      }

      return deepmerge(debtorModel.manual, {
        role: Role.FOREIGN_ORGANIZATION,
        foreignOrganization: {
          ...debtor.foreignOrganization,
          trustee: getDefaultManualTrustee(debtor.trustee),
        },
        fromEsia,
      });
    },

    getMachineModel({ debtor, fillType, isMrpa }) {
      if (fillType === SubjectFillType.MANUAL) {
        return cloneDeep(debtorModel.machine);
      }

      if (isMrpa) {
        prefillMrpDisabled(debtor);
      }

      const baseFields = {
        formVisible: true,
        mrpa: getMrpa(debtor),
      };

      if (debtor.person) {
        return deepmerge(debtorModel.machine, {
          ...baseFields,
          role: Role.PERSON,

          person: {
            ...debtor.person,
            trustee: getDefaultMachineTrustee(debtor.trustee),
          },
        });
      }

      if (debtor.organization) {
        return deepmerge(debtorModel.machine, {
          ...baseFields,
          role: Role.ORGANIZATION,
          organization: {
            ...debtor.organization,
            trustee: getDefaultMachineTrustee(debtor.trustee),
          },
        });
      }

      return deepmerge(debtorModel.machine, {
        ...baseFields,
        role: Role.FOREIGN_ORGANIZATION,
        foreignOrganization: {
          ...debtor.foreignOrganization,
          trustee: getDefaultMachineTrustee(debtor.trustee),
        },
      });
    },

    parsedMrpaChangeHandler(mrpa, i) {
      this.debtors[i].machine = mrpa
        ? this.getMachineModel({
            debtor: mrpa,
            fillType: SubjectFillType.MACHINE,
            isMrpa: true,
          })
        : cloneDeep(creditorModel.machine);
    },

    addDebtor() {
      this.debtors.push(cloneDeep(debtorModel));
    },

    deleteDebtor(index) {
      this.debtors = this.debtors.filter((_, i) => i !== Number(index));
    },

    async touchFields() {
      this.invalidFields = [];

      for (const debtor of this.templateDebtors) {
        debtor.$touch();

        if (debtor.$invalid) {
          debtor.expand.$model = true;
          this.invalidFields.push(true);
          continue;
        }

        if (
          debtor.fillType === SubjectFillType.MACHINE &&
          debtor.machine.mrpa.type === MrpaType.BY_GUID
        ) {
          const isValid = await this.checkMrpaGuid(
            debtor.machine.mrpa.guid,
            debtor.machine.mrpa.issuerInn
          );

          if (!isValid) this.invalidFields.push(true);
        }
      }
    },

    async checkMrpaGuid(guid, issuerInn) {
      try {
        await MrpaSubjectModule.checkMrpaSubjectGuid({
          statementType: StatementType.ACCEPTANCE_ESCROW,
          guid,
          issuerInn,
        });

        return true;
      } catch (e) {
        return false;
      }
    },

    assemblyModel() {
      return this.debtors.map((debtor) => {
        const cloneDebtor = cloneDeep(debtor);
        const baseModel = {
          uiContext: {
            fillType: cloneDebtor.fillType,
          },
        };

        if (cloneDebtor.fillType === SubjectFillType.MANUAL) {
          const { manual } = cloneDebtor;
          const { role } = manual;
          const { trustee } = manual[role];

          manual[role].trustee = null;

          return {
            ...baseModel,
            [role]: cloneDeep(manual[role]),
            details: {
              fromEsia: manual.fromEsia,
            },
            trustee: trustee ? assemblyManualTrustee(trustee) : undefined,
          };
        }

        const { machine } = cloneDebtor;
        const { role } = machine;
        const { trustee } = machine[role];

        machine[role].trustee = null;

        return {
          ...baseModel,
          [role]: cloneDeep(machine[role]),
          trustee: assemblyMrpaTrustee({
            trustee,
            mrpa: machine.mrpa,
          }),
          mrpType: machine.mrpa.type,
        };
      });
    },
  },
};
</script>

<style scoped lang="scss">
.debtor {
  &:not(:first-child) {
    margin-bottom: 40px;
  }
}
</style>
